import React, { useEffect } from "react";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import CustomDropdown from "../../components/customDropdown";
import MultiSelectComponent from "../../components/multiSelect";
import useReportsService from "./service";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { reportsSelector } from "../../redux/selectors/custom-selector";
import {
  clearReportsCompDetailResults,
  setCompPlanFilterValue,
  setCompPlanPeriodFilterValue,
  setEventTypeFilterValue,
  setIsFromProvider,
  setPeriodFilterValue,
  setPrefetchDropdownAction,
  setProviderFilterValue,
  setResultNameFilterValue,
} from "../../redux/actions/reports-actions";
import { getTenantCookieData } from "../../lib/cookies";

const Filters = () => {
  const dispatch = useDispatch();
  const {
    compPlanFilterData,
    compPlanFilterValue,
    isCompPlanFilterDataLoading,

    compPlanPeriodFilterData,
    compPlanPeriodFilterValue,
    isCompPlanPeriodFilterDataLoading,

    providerFilterData,
    providerFilterValue,
    isProviderFilterDataLoading,

    periodFilterData,
    periodFilterValue,
    isPeriodFilterDataLoading,

    eventTypeFilterData,
    eventTypeFilterValue,
    isEventTypeFilterDataLoading,

    resultNameFilterData,
    resultNameFilterValue,
    isResultNameFilterDataLoading,

    isFromProvider,
    prefetchDropdowns,
    orgTitleFilterValue,
  } = useSelector(reportsSelector, shallowEqual);
  const reportsServiceActions = useReportsService();

  const [activeCompPlanList, setActiveCompPlanList] = React.useState(compPlanFilterData || []);
  const [activeCompPlanPeriodList, setActiveCompPlanPeriodList] = React.useState(compPlanPeriodFilterData || []);
  const [activeProviderList, setActiveProviderList] = React.useState(providerFilterData || []);
  const [activePeriodList, setActivePeriodList] = React.useState(periodFilterData || []);
  const [activeEventTypeList, setActiveEventTypeList] = React.useState(eventTypeFilterData || []);
  const [activeResultNameList, setActiveResultNameList] = React.useState([]);

  const defaultPageSize = getTenantCookieData("record_per_page");

  React.useEffect(() => {
    if (compPlanFilterData?.length > 0) {
      setActiveCompPlanList(compPlanFilterData);
    } else {
      reportsServiceActions.getCompPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanFilterData]);

  useEffect(() => {
    setActiveCompPlanPeriodList(compPlanPeriodFilterData);
  }, [compPlanPeriodFilterData]);

  useEffect(() => {
    setActiveProviderList(providerFilterData);
  }, [providerFilterData]);

  useEffect(() => {
    setActivePeriodList(periodFilterData);
  }, [periodFilterData]);

  useEffect(() => {
    setActiveEventTypeList(eventTypeFilterData);
  }, [eventTypeFilterData]);

  useEffect(() => {
    // Store mapped items (with composite_key + display_name) so the MultiSelect's
    // textField / dataItemKey lookups don't blow up on raw data.
    const mapped = (resultNameFilterData || []).map((r) => ({
      ...r,
      composite_key: `${r.result_name_mstr_key}_${r.result_type_mstr_key ?? ""}`,
      display_name: r.result_type_name ? `${r.result_name} | ${r.result_type_name}` : r.result_name,
    }));
    setActiveResultNameList(mapped);
  }, [resultNameFilterData]);

  useEffect(() => {
    reportsServiceActions.getCompPlanPeriods(compPlanFilterValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanFilterValue]);

  const lastFetchedCompPlanProviderRef = React.useRef(null);
  useEffect(() => {
    const compPlanKey = compPlanFilterValue?.comp_plan_key || 0;
    const periodKey = compPlanPeriodFilterValue?.period_key || 0;
    const compositeKey = [compPlanKey, periodKey].join("|");
    if (compPlanPeriodFilterValue && lastFetchedCompPlanProviderRef.current !== compositeKey) {
      reportsServiceActions.getCompPlanProviders(compPlanPeriodFilterValue);
      reportsServiceActions.getPeriod(compPlanFilterValue, compPlanPeriodFilterValue);
      lastFetchedCompPlanProviderRef.current = compositeKey;
    } else {
      lastFetchedCompPlanProviderRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanPeriodFilterValue]);

  useEffect(() => {
    if (prefetchDropdowns && !eventTypeFilterData.length) {
      reportsServiceActions.getEventTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (eventTypeFilterData.length > 0) {
  //     const found = eventTypeFilterData.find(
  //       (it) =>
  //         it.event_type_mstr_key === eventTypeFilterValue?.event_type_mstr_key
  //     );

  //     dispatch(setEventTypeFilterValue(found || eventTypeFilterData[0]));
  //   } else {
  //     reportsServiceActions.getEventTypes();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [eventTypeFilterData]);

  const handleChangeCompPlan = (value) => {
    dispatch(setPrefetchDropdownAction(true));
    dispatch(setCompPlanFilterValue(value));
  };

  const handleChangeCompPlanPeriod = (value) => {
    dispatch(setCompPlanPeriodFilterValue(value));
  };

  const handleChangeProvider = (value) => {
    dispatch(setProviderFilterValue(value));
  };

  const handleChangePeriod = (value) => {
    dispatch(setPeriodFilterValue(value));
  };

  const handleChangeEventType = (value) => {
    dispatch(setEventTypeFilterValue(value));
  };

  const handleChangeResultName = (value) => {
    if (!value || value.length === 0) {
      dispatch(setResultNameFilterValue([]));
      return;
    }
    const realItems = value.filter((v) => v.result_name_mstr_key !== 0);
    dispatch(setResultNameFilterValue(realItems));
  };

  useEffect(() => {
    const compPlanKey = compPlanFilterValue?.comp_plan_key;
    const periodKey = compPlanPeriodFilterValue?.period_key;
    const providerKey = providerFilterValue?.provider_key;
    const eventTypeKey = eventTypeFilterValue?.event_type_mstr_key;
    if (compPlanKey && periodKey && providerKey && eventTypeKey) {
      if (!isFromProvider) {
        dispatch(setResultNameFilterValue([]));
      }
      reportsServiceActions.getResultNames(compPlanKey, providerKey, periodKey, eventTypeKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    compPlanFilterValue?.comp_plan_key,
    compPlanPeriodFilterValue?.period_key,
    providerFilterValue?.provider_key,
    eventTypeFilterValue?.event_type_mstr_key,
  ]);

  useEffect(() => {
    if (!resultNameFilterData?.length || !resultNameFilterValue?.length) return;
    const byComposite = new Map(
      resultNameFilterData.map((r) => [`${r.result_name_mstr_key}_${r.result_type_mstr_key ?? ""}`, r])
    );
    const byNameKey = new Map();
    resultNameFilterData.forEach((r) => {
      if (!byNameKey.has(r.result_name_mstr_key)) byNameKey.set(r.result_name_mstr_key, r);
    });
    const reconciled = resultNameFilterValue
      .map((v) => {
        const composite = `${v.result_name_mstr_key}_${v.result_type_mstr_key ?? ""}`;
        return byComposite.get(composite) || byNameKey.get(v.result_name_mstr_key);
      })
      .filter(Boolean);
    const changed =
      reconciled.length !== resultNameFilterValue.length ||
      reconciled.some((h, i) => h !== resultNameFilterValue[i]);
    if (changed) {
      dispatch(setResultNameFilterValue(reconciled));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultNameFilterData]);

  const buildCompositeKey = (r) => `${r.result_name_mstr_key}_${r.result_type_mstr_key ?? ""}`;

  const SELECT_ALL_SENTINEL = {
    result_name_mstr_key: 0,
    result_type_mstr_key: null,
    result_name: "Select All",
    result_type_name: "",
    composite_key: "0_",
    display_name: "Select All",
  };

  const resultNameOptions = (resultNameFilterData || []).map((r) => ({
    ...r,
    composite_key: buildCompositeKey(r),
    display_name: r.result_type_name ? `${r.result_name} | ${r.result_type_name}` : r.result_name,
  }));

  const hydratedSelections = (resultNameFilterValue || []).filter((v) => v.result_name);
  const resultNameValue = (() => {
    if (hydratedSelections.length > 0) {
      return hydratedSelections.map((v) => ({
        ...v,
        composite_key: buildCompositeKey(v),
        display_name: v.result_type_name ? `${v.result_name} | ${v.result_type_name}` : v.result_name,
      }));
    }
    // No real selection AND options loaded → show the Select All chip as default state
    if (resultNameOptions.length > 0 && (resultNameFilterValue || []).length === 0) {
      return [SELECT_ALL_SENTINEL];
    }
    // No data loaded yet, or pre-hydration drill-through → render no chip
    return [];
  })();

  useEffect(() => {
    if (
      compPlanFilterValue?.comp_plan_key &&
      compPlanPeriodFilterValue?.period_key &&
      providerFilterValue?.provider_key &&
      periodFilterValue?.period_key &&
      eventTypeFilterValue?.event_type_mstr_key &&
      isFromProvider &&
      !isCompPlanFilterDataLoading &&
      !isCompPlanPeriodFilterDataLoading &&
      !isProviderFilterDataLoading &&
      !isPeriodFilterDataLoading &&
      !isEventTypeFilterDataLoading
    ) {
      dispatch(setIsFromProvider(false));
      runReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    compPlanFilterValue,
    compPlanPeriodFilterValue,
    providerFilterValue,
    periodFilterValue,
    isFromProvider,
    eventTypeFilterValue,
    isCompPlanFilterDataLoading,
    isCompPlanPeriodFilterDataLoading,
    isProviderFilterDataLoading,
    isPeriodFilterDataLoading,
    isEventTypeFilterDataLoading,
  ]);

  const runReports = () => {
    dispatch(setPrefetchDropdownAction(false));
    reportsServiceActions.getAutoHideSection1();
    getSection1();
    getSection2();
  };

  const getSection1 = () => {
    reportsServiceActions.getSection1(providerFilterValue["provider_key"], periodFilterValue["period_key"]);
  };

  const getSection2 = () => {
    let sortField = "1 ASC";
    dispatch(clearReportsCompDetailResults(defaultPageSize));
    reportsServiceActions.getSection2(
      providerFilterValue["provider_key"],
      eventTypeFilterValue["event_type_mstr_key"],
      compPlanFilterValue["comp_plan_key"],
      periodFilterValue["period_key"],
      periodFilterValue["period_start_date"],
      periodFilterValue["period_end_date"],
      0,
      defaultPageSize.recordTake,
      sortField,
      null,
      true,
      orgTitleFilterValue,
      Array.from(new Set((resultNameFilterValue || []).map((r) => r.result_name_mstr_key))).join(",")
    );
  };

  return (
    <ul className="CompReportFilter-links reports-filters">
      <div className={"k-form-field-wrap-popup valueDropdown width250"}>
        <Label>{"Comp Plan:"}</Label>
        <CustomDropdown
          name="compPlanDropdown"
          data={activeCompPlanList}
          textField="comp_plan_name"
          dataItemKey="comp_plan_key"
          loading={isCompPlanFilterDataLoading}
          valueData={compPlanFilterValue}
          onChange={(value) => handleChangeCompPlan(value)}
          filterable={true}
          dataList={compPlanFilterData}
          onClose={() => {
            setActiveCompPlanList(compPlanFilterData);
          }}
          onFilterChange={(dataValue) => setActiveCompPlanList(dataValue)}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown width250"} style={{ flex: "0 0 170px", width: 170 }}>
        <Label>{"Comp Plan Period:"}</Label>
        <CustomDropdown
          name="compPlanPeriodDropdown"
          data={activeCompPlanPeriodList}
          textField="period_name"
          onChange={(value) => handleChangeCompPlanPeriod(value)}
          dataItemKey="period_key"
          loading={isCompPlanPeriodFilterDataLoading}
          valueData={compPlanPeriodFilterValue}
          filterable={true}
          dataList={compPlanPeriodFilterData}
          onFilterChange={(dataValue) => {
            setActiveCompPlanPeriodList(dataValue);
          }}
          onClose={() => {
            setActiveCompPlanPeriodList(compPlanPeriodFilterData);
          }}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown width250"}>
        <Label>{"Provider:"}</Label>
        <CustomDropdown
          name="providerDropdown"
          data={activeProviderList}
          textField="provider_name"
          onChange={(value) => handleChangeProvider(value)}
          dataItemKey="provider_key"
          loading={isProviderFilterDataLoading}
          valueData={providerFilterValue}
          filterable={true}
          dataList={providerFilterData}
          onClose={() => {
            setActiveProviderList(providerFilterData);
          }}
          onFilterChange={(dataValue) => setActiveProviderList(dataValue)}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown width250"}>
        <Label>{"Period:"}</Label>
        <CustomDropdown
          name="periodDropdown"
          data={activePeriodList}
          textField="period_name"
          onChange={(value) => handleChangePeriod(value)}
          dataItemKey="period_key"
          loading={isPeriodFilterDataLoading}
          valueData={periodFilterValue}
          filterable={true}
          dataList={periodFilterData}
          onClose={() => {
            setActivePeriodList(periodFilterData);
          }}
          onFilterChange={(dataValue) => setActivePeriodList(dataValue)}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown"}>
        <Label>{"Event Type:"}</Label>
        <CustomDropdown
          name="eventTypeDropdown"
          data={activeEventTypeList}
          textField="event_type_name"
          onChange={(value) => handleChangeEventType(value)}
          dataItemKey="event_type_mstr_key"
          loading={isEventTypeFilterDataLoading}
          valueData={eventTypeFilterValue}
          filterable={true}
          dataList={eventTypeFilterData}
          onClose={() => {
            setActiveEventTypeList(eventTypeFilterData);
          }}
          onFilterChange={(dataValue) => setActiveEventTypeList(dataValue)}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown width250"} style={{ flex: "0 0 330px", width: 330 }}>
        <Label>{"Result Name | Result Type"}</Label>
        <MultiSelectComponent
          name="resultNameDropdown"
          data={activeResultNameList}
          dataList={resultNameOptions}
          textField="display_name"
          dataItemKey="composite_key"
          loading={isResultNameFilterDataLoading}
          valueData={resultNameValue}
          onChange={(value) => handleChangeResultName(value)}
          filterable={true}
          onFilterChange={(dataValue) => setActiveResultNameList(dataValue)}
          onClose={() => setActiveResultNameList(resultNameOptions)}
          autoClose={false}
          isSelectALl={false}
        />
      </div>
      <div className="k-form-buttons btn-comp-report-filter">
        <Button
          primary={true}
          disabled={
            !compPlanFilterValue ||
            !compPlanPeriodFilterValue ||
            !providerFilterValue ||
            !periodFilterValue ||
            !eventTypeFilterValue ||
            isCompPlanFilterDataLoading ||
            isCompPlanPeriodFilterDataLoading ||
            isPeriodFilterDataLoading ||
            isProviderFilterDataLoading ||
            isEventTypeFilterDataLoading
          }
          type={"submit"}
          onClick={() => runReports()}
          className="primarybtn"
        >
          Run
        </Button>
      </div>
    </ul>
  );
};

export default React.memo(Filters);
